import { InvoiceReportsSearchRefetchProvider } from '@/components/InvoiceReportDetailDrawer/context/InvoiceReportsSearchRefetchContext';
import type { CheckPermissionFn } from '@/components/InvoiceReportDetailDrawer/type';
import { ErrorBoundary } from '@/components/Rollbar';
import {
  ConfirmCancelModal,
  ConfirmCancelModalProvider,
  useConfirmCancelModal,
} from '@/features/invoice_reports/components/ConfirmCancelModal';
import {
  ConfirmBulkDeleteModal,
  ConfirmDeleteModalProvider,
  ConfirmSingleDeleteModal,
  SingleDeleteRecord,
  useConfirmDeleteModal,
} from '@/features/invoice_reports/components/ConfirmDeleteModal';
import {
  ConfirmDuplicateModal,
  ConfirmDuplicateModalProvider,
  useConfirmDuplicateModal,
} from '@/features/invoice_reports/components/ConfirmDuplicateModal';
import { InvoiceReportDetailDrawer } from '@/features/invoice_reports/components/InvoiceReportDetailDrawer/Drawer';
import { useViewInvoiceReportDetail } from '@/features/invoice_reports/components/InvoiceReportDetailDrawer/hooks/useViewInvoiceReportDetail';
import {
  FetchInvoiceReportsArgs,
  InvoiceReportColumn,
  InvoiceReportsSearchQuery,
} from '@/features/invoice_reports/types';
import type { DuplicateParams } from '@/features/invoice_reports/components/ConfirmDuplicateModal/context/ConfirmDuplicateModalContext';
import type { CancelRecord } from '@/features/invoice_reports/components/ConfirmCancelModal/context/ConfirmCancelModalContext';
import { ErrorInvoiceReportsTable } from './ErrorInvoiceReportsTable';
import { LoadingInvoiceReportsTable } from './LoadingInvoiceReportsTable';

import {
  ModernPaginationProps,
  Table,
  TableProps,
} from '@moneyforward/ap-frontend-components';

import classnames from 'classnames/bind';
import { FC, memo, Suspense, useCallback, useEffect, useMemo } from 'react';
import { TriggerFindTargetReportProvider } from '../context/TriggerFindTargetReportContext';
import { useEmptyState, useTableSelection } from '../hooks';
import { getTotalTableWidth, useColumns } from './Columns';
import { useResizableColumns } from './hooks/useResizableColumns';
import { useTableHeight } from './hooks/useTableHeight';
import styles from './Table.module.scss';
import {
  isValidSortState,
  MultiColumnSortState,
  SortOrderState,
} from './types';

const cx = classnames.bind(styles);

export type InternalTableProps = Omit<
  TableProps<InvoiceReportColumn>,
  'columns' | 'pagination' | 'emptyType'
> & {
  sortOrders?: MultiColumnSortState[];
  onMultiColumnSort?: (sortStates: MultiColumnSortState[]) => void;
};

export type HandleSelectRowProps = {
  handleSelectAll?: (checked: boolean) => void;
  isAllSelected?: boolean;
  selectedRowKeys?: string[];
  handleSelectRow?: (record: InvoiceReportColumn, checked: boolean) => void;
  isAllDisabled?: boolean;
};

export type Props = HandleSelectRowProps & {
  pagination: ModernPaginationProps;
  table: InternalTableProps;
  isSearchEmpty?: boolean;
  sortOrders?: MultiColumnSortState[];
  isLoading?: boolean;
  onMultiColumnSort?: (sortStates: MultiColumnSortState[]) => void;
  canAgentEntry?: boolean;
  onDelete?: (record: SingleDeleteRecord) => void;
  onCancel?: (record: CancelRecord) => void;
  onDuplicate?: (params: DuplicateParams) => Promise<void>;
};

// Empty callback constants to avoid creating new functions on each render
const EMPTY_SELECT_ALL_CALLBACK = () => {};
const EMPTY_SELECT_ROW_CALLBACK = () => {};
const EMPTY_SELECTED_ROW_KEYS: string[] = [];

export const InvoiceReportsTable: FC<Props> = memo(
  ({
    pagination,
    table,
    isSearchEmpty = true,
    sortOrders,
    isLoading = false,
    selectedRowKeys = EMPTY_SELECTED_ROW_KEYS,
    handleSelectRow = EMPTY_SELECT_ROW_CALLBACK,
    handleSelectAll = EMPTY_SELECT_ALL_CALLBACK,
    isAllSelected = false,
    isAllDisabled = false,
    onMultiColumnSort,
    canAgentEntry = false,
    onDelete,
    onCancel,
    onDuplicate,
  }) => {
    const { columnWidths, handleResize } = useResizableColumns();

    const columns = useColumns({
      selectedRowKeys,
      onSelectAll: handleSelectAll,
      onSelectRow: handleSelectRow,
      isAllSelected,
      isAllDisabled,
      isLoading,
      columnWidths,
      canAgentEntry,
      onDelete,
      onCancel,
      onDuplicate,
    });

    const { refContainer, listHeight } = useTableHeight({
      marginHeight: 100,
    });

    const handleColumnResize = useCallback(
      (columnIndex: number, width: number) => {
        handleResize(columnIndex, width, columns);
      },
      [handleResize, columns]
    );

    const resizableProps = useMemo(
      () => ({ resizable: true as const, onResize: handleColumnResize }),
      [handleColumnResize]
    );

    const { emptyType, emptyMessage } = useEmptyState(isSearchEmpty);

    const dynamicTableWidth = useMemo(() => {
      return getTotalTableWidth(columnWidths, canAgentEntry);
    }, [columnWidths, canAgentEntry]);

    // Memoize scroll config with primitive dependencies
    const scrollY = `${listHeight}px`;
    const scroll = useMemo(
      () => ({
        ...(dynamicTableWidth !== undefined && { x: dynamicTableWidth }),
        y: scrollY,
      }),
      [dynamicTableWidth, scrollY]
    );

    // Wrapper to handle the onMultiColumnSort call
    // The Table component passes sort states that we need to convert
    const handleMultiColumnSortWrapper = useCallback(
      (sortStates: unknown) => {
        if (!onMultiColumnSort) return;

        // Handle both array and single state (for type compatibility)
        const statesArray = Array.isArray(sortStates)
          ? sortStates
          : [sortStates];

        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const sortOrderStates = statesArray as SortOrderState[];
        const convertedSorts: MultiColumnSortState[] = sortOrderStates
          .filter(isValidSortState)
          .map((s) => ({
            columnKey: s.columnKey,
            order: s.order,
          }));
        onMultiColumnSort(convertedSorts);
      },
      [onMultiColumnSort]
    );

    // Memoize container class to avoid recalculation
    const hasData = (table?.data?.length ?? 0) > 0;
    const containerClassName = cx(
      styles['container'],
      hasData && styles['reports-has-data']
    );

    return (
      <div
        className={containerClassName}
        ref={refContainer}
        data-testid='invoice-reports-table'
      >
        <Table
          emptyType={emptyType}
          {...table}
          legacy={false}
          key={pagination.currentPage}
          testId='invoice-reports-table'
          emptyText={emptyMessage}
          columns={columns}
          pagination={pagination}
          hideHeaderPagination
          scroll={scroll}
          loading={isLoading}
          tableLayout='fixed'
          isMultiColumnSortEnabled
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
          onMultiColumnSort={handleMultiColumnSortWrapper as any}
          initialSortState={sortOrders}
          {...resizableProps}
        />
      </div>
    );
  }
);
InvoiceReportsTable.displayName = 'InvoiceReportsTable';

export type InvoiceReportsTableProps = Omit<
  TableProps<InvoiceReportColumn>,
  'columns' | 'pagination' | 'emptyType'
>;

export type InvoiceReportsTableViewProps = {
  searchQuery: InvoiceReportsSearchQuery;
  onSearch: (value: InvoiceReportsSearchQuery) => void;
  onMultiColumnSort?: (sortStates: MultiColumnSortState[]) => void;
  sortOrders?: MultiColumnSortState[];
  query: FetchInvoiceReportsArgs;
  data: InvoiceReportColumn[];
  pagination: ModernPaginationProps;
  isFetching: boolean;
  canAgentEntry?: boolean;
  refetch: CheckPermissionFn;
  onSelectionChange?: (count: number, ids: string[]) => void;
  bulkDeleteModalOpen: boolean;
  bulkDeleteCount: number;
  bulkDeleteIds: string[];
  onCloseBulkDeleteModal: () => void;
  /** Whether the office is duty-free, used to exclude tax-related columns from the transactions table */
  isDutyFree: boolean;
};

const InnerListContent: FC<InvoiceReportsTableViewProps> = memo(
  ({
    searchQuery,
    onSearch,
    onMultiColumnSort,
    sortOrders,
    query,
    data,
    pagination: basePagination,
    isFetching,
    canAgentEntry = false,
    onSelectionChange,
    bulkDeleteModalOpen,
    bulkDeleteCount,
    bulkDeleteIds,
    onCloseBulkDeleteModal,
    refetch,
    isDutyFree,
  }) => {
    const {
      selectedRowKeys,
      handleSelectRow,
      handleSelectAll,
      isAllSelected,
      selectedRowData,
      isAllDisabled,
    } = useTableSelection(data);

    const {
      onDrawerOpen,
      onDrawerClose,
      selectedId,
      selectedIndex,
      setSelectedIndex,
    } = useViewInvoiceReportDetail();

    // Clear selections when loading starts
    const selectedCount = selectedRowData.length;
    useEffect(() => {
      if (isFetching && selectedCount > 0) {
        handleSelectAll(false);
      }
    }, [isFetching, selectedCount, handleSelectAll]);

    useEffect(() => {
      onSelectionChange?.(
        selectedCount,
        selectedRowData.map((r) => r.report_id)
      );
    }, [selectedCount, selectedRowData, onSelectionChange]);

    // Memoize page change handler
    const onPageChange = useCallback(
      (page: number) => onSearch({ page }),
      [onSearch]
    );

    // Use primitive value for currentPage dependency
    const currentPage = searchQuery.page ?? 1;
    const pagination = useMemo(
      () => ({
        ...basePagination,
        onChange: onPageChange,
        currentPage,
      }),
      [basePagination, onPageChange, currentPage]
    );

    const isSearchEmpty = useMemo(() => {
      const { query: _query } = query;
      return data.length === 0 && Object.keys(_query ?? {}).length !== 0;
    }, [data.length, query]);

    /** Effect to sync selectedIndex with selectedId and data */
    useEffect(() => {
      if (selectedIndex !== undefined) return;
      if (!data.length) {
        setSelectedIndex(0);
        return;
      }
      if (selectedId) {
        const currentIndex = data.findIndex((item) => item.id === selectedId);
        currentIndex === -1
          ? setSelectedIndex(0)
          : setSelectedIndex(currentIndex);
      }
    }, [selectedId, data, setSelectedIndex, selectedIndex]);

    // Memoize table config with stable callbacks
    const table: InvoiceReportsTableProps = useMemo(
      () => ({
        data,
        onRow: (record, index) => ({
          onClick: () => {
            onDrawerOpen(record.id);
            setSelectedIndex(index);
          },
          className: cx({
            [styles['selected-row']]:
              selectedId === record.id || selectedRowKeys.includes(record.id),
          }),
        }),
      }),
      [data, onDrawerOpen, selectedId, selectedRowKeys, setSelectedIndex]
    );

    return (
      <InvoiceReportsSearchRefetchProvider value={refetch}>
        <TriggerFindTargetReportProvider
          data={data}
          onSearch={onSearch}
          pagination={pagination}
          selectedIndex={selectedIndex}
          setSelectedIndex={setSelectedIndex}
          isDataLoading={isFetching}
        >
          <ConfirmDeleteModalProvider
            refetch={refetch}
            onDrawerOpen={onDrawerOpen}
            onDrawerClose={onDrawerClose}
            selectedId={selectedId}
          >
            <ConfirmCancelModalProvider
              refetch={refetch}
              onDrawerClose={onDrawerClose}
              selectedId={selectedId}
            >
              <InnerListTable
                pagination={pagination}
                table={table}
                sortOrders={sortOrders}
                isFetching={isFetching}
                isSearchEmpty={isSearchEmpty}
                selectedRowKeys={selectedRowKeys}
                handleSelectRow={handleSelectRow}
                handleSelectAll={handleSelectAll}
                isAllSelected={isAllSelected}
                isAllDisabled={isAllDisabled || isFetching}
                onMultiColumnSort={onMultiColumnSort}
                canAgentEntry={canAgentEntry}
                selectedId={selectedId}
                selectedApPayeeName={
                  data.find((r) => r.id === selectedId)?.ap_payee_name
                }
                onDrawerClose={onDrawerClose}
                bulkDeleteModalOpen={bulkDeleteModalOpen}
                bulkDeleteCount={bulkDeleteCount}
                bulkDeleteIds={bulkDeleteIds}
                onCloseBulkDeleteModal={onCloseBulkDeleteModal}
                isDutyFree={isDutyFree}
              />
            </ConfirmCancelModalProvider>
          </ConfirmDeleteModalProvider>
        </TriggerFindTargetReportProvider>
      </InvoiceReportsSearchRefetchProvider>
    );
  }
);
InnerListContent.displayName = 'InnerListContent';

type InnerListTableProps = {
  pagination: ModernPaginationProps;
  table: InvoiceReportsTableProps;
  sortOrders?: MultiColumnSortState[];
  isFetching: boolean;
  isSearchEmpty: boolean;
  selectedRowKeys: string[];
  handleSelectRow: (record: InvoiceReportColumn, checked: boolean) => void;
  handleSelectAll: (checked: boolean) => void;
  isAllSelected: boolean;
  isAllDisabled: boolean;
  onMultiColumnSort?: (sortStates: MultiColumnSortState[]) => void;
  canAgentEntry: boolean;
  selectedId: string | null;
  selectedApPayeeName?: string | null;
  onDrawerClose: () => void;
  bulkDeleteModalOpen: boolean;
  bulkDeleteCount: number;
  bulkDeleteIds: string[];
  onCloseBulkDeleteModal: () => void;
  /** Whether the office is duty-free, used to exclude tax-related columns from the transactions table */
  isDutyFree: boolean;
};

const InnerListTable: FC<InnerListTableProps> = memo(
  ({
    pagination,
    table,
    sortOrders,
    isFetching,
    isSearchEmpty,
    selectedRowKeys,
    handleSelectRow,
    handleSelectAll,
    isAllSelected,
    isAllDisabled,
    onMultiColumnSort,
    canAgentEntry,
    selectedId,
    selectedApPayeeName,
    onDrawerClose,
    bulkDeleteModalOpen,
    bulkDeleteCount,
    bulkDeleteIds,
    onCloseBulkDeleteModal,
    isDutyFree,
  }) => {
    const { onOpenConfirmDeleteModal } = useConfirmDeleteModal();
    const { onOpenConfirmCancelModal } = useConfirmCancelModal();
    const { onOpenConfirmDuplicateModal } = useConfirmDuplicateModal();

    return (
      <>
        <InvoiceReportsTable
          pagination={pagination}
          table={table}
          sortOrders={sortOrders}
          isLoading={isFetching}
          isSearchEmpty={isSearchEmpty}
          selectedRowKeys={selectedRowKeys}
          handleSelectRow={handleSelectRow}
          handleSelectAll={handleSelectAll}
          isAllSelected={isAllSelected}
          isAllDisabled={isAllDisabled}
          onMultiColumnSort={onMultiColumnSort}
          canAgentEntry={canAgentEntry}
          onDelete={onOpenConfirmDeleteModal}
          onCancel={onOpenConfirmCancelModal}
          onDuplicate={onOpenConfirmDuplicateModal}
        />
        {selectedId && (
          <InvoiceReportDetailDrawer
            open={!!selectedId}
            onClose={onDrawerClose}
            selectedId={selectedId}
            isDutyFree={isDutyFree}
            apPayeeName={selectedApPayeeName}
          />
        )}
        <ConfirmSingleDeleteModal />
        <ConfirmDuplicateModal />
        <ConfirmCancelModal />
        <ConfirmBulkDeleteModal
          bulkDeleteModalOpen={bulkDeleteModalOpen}
          bulkDeleteCount={bulkDeleteCount}
          bulkDeleteIds={bulkDeleteIds}
          onCloseBulkDeleteModal={onCloseBulkDeleteModal}
        />
      </>
    );
  }
);
InnerListTable.displayName = 'InnerListTable';

export const InvoiceReportsTableView: FC<InvoiceReportsTableViewProps> = memo(
  (props) => {
    return (
      <ErrorBoundary fallback={ErrorInvoiceReportsTable}>
        <Suspense fallback={<LoadingInvoiceReportsTable />}>
          <ConfirmDuplicateModalProvider>
            <InnerListContent {...props} />
          </ConfirmDuplicateModalProvider>
        </Suspense>
      </ErrorBoundary>
    );
  }
);
InvoiceReportsTableView.displayName = 'InvoiceReportsTableView';
